import React, { useEffect } from 'react';
import { FormField, FormSection, FormCheckbox, FormSelect } from '../../../shared/FormComponents';
import { TOOLTIPS, FILING_STATUS_OPTIONS } from '../../../shared/constants';
import { calculateTaxSummary, formatTaxSummary } from '../../../shared/taxCalculator';
import { formatCurrency, parseCurrency } from '../../../../utils/incomeUtils';

const TaxPlanningSection = ({ register, errors, watch, setValue }) => {
  const grossPay = watch('grossPay');
  const frequency = watch('frequency');
  const state = watch('state');
  const taxSummary = watch('taxSummary') || {}; // Get the tax summary from form state
  
  // Format currency inputs for business expenses
  const handleBusinessExpensesChange = (e) => {
    const field = e.target.name;
    const value = e.target.value;
    // Remove non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setValue(field, cleanValue);
  };

  const handleBusinessExpensesBlur = (e) => {
    const field = e.target.name;
    const value = e.target.value;
    if (!value) {
      setValue(field, '');
      return;
    }

    // Format with two decimal places
    try {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setValue(field, numValue.toFixed(2));
      }
    } catch (error) {
      console.error('Error formatting business expense:', error);
    }
  };
  
  // Calculate SE tax (15.3% of 92.35% of net earnings)
  const calculateSETax = () => {
    if (!grossPay || !frequency) return '0.00';
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return '0.00';
    
    // Calculate SE tax base (92.35% of earnings)
    const seTaxBase = numGrossPay * 0.9235;
    // Calculate SE tax (15.3%)
    const seTax = seTaxBase * 0.153;
    
    return seTax.toFixed(2);
  };
  
  // Calculate estimated quarterly tax payment
  const calculateQuarterlyTax = () => {
    if (!grossPay || !frequency) return '0.00';
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return '0.00';
    
    // Estimate income tax (25% as a simple estimate)
    const incomeTax = numGrossPay * 0.25;
    
    // Get SE tax
    const seTax = parseFloat(calculateSETax());
    
    // Total estimated tax
    const totalTax = incomeTax + seTax;
    
    // Convert to quarterly based on frequency
    let annualTax = 0;
    switch (frequency) {
      case 'weekly':
        annualTax = totalTax * 52;
        break;
      case 'biweekly':
        annualTax = totalTax * 26;
        break;
      case 'monthly':
        annualTax = totalTax * 12;
        break;
      case 'quarterly':
        annualTax = totalTax * 4;
        break;
      case 'yearly':
        annualTax = totalTax;
        break;
      default:
        annualTax = totalTax * 12; // Default to monthly
    }
    
    // Quarterly payment is 1/4 of annual tax
    const quarterlyPayment = annualTax / 4;
    
    return quarterlyPayment.toFixed(2);
  };
  
  // Calculate take-home pay after taxes
  const calculateTakeHomePay = () => {
    if (!grossPay || !frequency) return '0.00';
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return '0.00';
    
    // Estimate income tax (25% as a simple estimate)
    const incomeTax = numGrossPay * 0.25;
    
    // Get SE tax
    const seTax = parseFloat(calculateSETax());
    
    // Calculate take-home pay
    const takeHomePay = numGrossPay - incomeTax - seTax;
    
    return takeHomePay.toFixed(2);
  };
  
  // Calculate recommended retirement contribution
  const calculateRetirementContribution = () => {
    if (!grossPay || !frequency) return '0.00';
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return '0.00';
    
    // Recommend 15% of gross income for retirement
    const recommendedContribution = numGrossPay * 0.15;
    
    return recommendedContribution.toFixed(2);
  };

  // Calculate monthly gross pay based on frequency
  const calculateMonthlyGrossPay = () => {
    if (!grossPay || !frequency) return 0;
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return 0;

    switch (frequency) {
      case 'weekly':
        return numGrossPay * 52 / 12;
      case 'biweekly':
        return numGrossPay * 26 / 12;
      case 'monthly':
        return numGrossPay;
      case 'quarterly':
        return numGrossPay / 3;
      case 'yearly':
        return numGrossPay / 12;
      default:
        return numGrossPay;
    }
  };

  const calculateHomeOfficeDeduction = () => {
    const homeOfficeSqft = parseFloat(watch('homeOfficeSqft') || 0);
    const totalHomeSqft = parseFloat(watch('totalHomeSqft') || 0);
    
    if (!homeOfficeSqft || !totalHomeSqft || totalHomeSqft === 0) return 0;
    
    // Calculate the percentage of home used for business
    const businessUsePercentage = (homeOfficeSqft / totalHomeSqft) * 100;
    
    // Get monthly home expenses
    const monthlyHomeExpenses = parseFloat(watch('monthlyHomeExpenses') || 0);
    
    // Calculate deduction (percentage of home expenses)
    return (monthlyHomeExpenses * businessUsePercentage) / 100;
  };

  // Calculate business expenses based on frequency
  const calculateTotalBusinessExpenses = () => {
    const expenses = [
      'equipmentExpenses',
      'professionalServices',
      'travelExpenses',
      'marketingExpenses',
      'healthInsurance',
      'otherExpenses',
      'retirementContribution',
      'hsaContribution'
    ];
    
    const total = expenses.reduce((sum, expense) => {
      const value = parseFloat(watch(expense) || 0);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
    
    return total + calculateHomeOfficeDeduction();
  };

  // Get frequency label for tooltips
  const getFrequencyLabel = () => {
    if (!frequency) return 'per pay period';
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        return 'weekly';
      case 'biweekly':
        return 'biweekly';
      case 'monthly':
        return 'monthly';
      case 'quarterly':
        return 'quarterly';
      case 'yearly':
        return 'yearly';
      default:
        return 'per pay period';
    }
  };

  // Convert business expenses to monthly equivalent for calculations
  const convertToMonthlyExpenses = (amount) => {
    if (!frequency || !amount) return 0;
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 0;
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        return numAmount * 52 / 12;
      case 'biweekly':
        return numAmount * 26 / 12;
      case 'monthly':
        return numAmount;
      case 'quarterly':
        return numAmount / 3;
      case 'yearly':
        return numAmount / 12;
      default:
        return numAmount;
    }
  };

  // Calculate total monthly business expenses (for tax calculations)
  const calculateMonthlyBusinessExpenses = () => {
    const expenses = [
      'equipmentExpenses',
      'professionalServices',
      'travelExpenses',
      'marketingExpenses',
      'healthInsurance',
      'otherExpenses',
      'retirementContribution',
      'hsaContribution'
    ];
    
    const total = expenses.reduce((sum, expense) => {
      const value = parseFloat(watch(expense) || 0);
      return sum + convertToMonthlyExpenses(value);
    }, 0);
    
    return total + calculateHomeOfficeDeduction();
  };

  // Update total business expenses whenever individual expenses change
  useEffect(() => {
    if (grossPay && frequency && state) {
      const totalExpenses = calculateTotalBusinessExpenses();
      setValue('totalBusinessExpenses', totalExpenses.toFixed(2));
      
      // Update tax calculations with the new business expenses
      const businessExpenses = calculateMonthlyBusinessExpenses() * 12; // Annualize the monthly expenses
      
      // Map frequency to the expected format
      const frequencyMap = {
        'weekly': 'weekly',
        'biweekly': 'biweekly',
        'semimonthly': 'semimonthly',
        'monthly': 'monthly',
        'quarterly': 'quarterly',
        'yearly': 'yearly'
      };
      
      // Recalculate tax summary with business expenses
      const updatedTaxSummary = calculateTaxSummary({
        grossPay: parseFloat(grossPay),
        frequency: frequencyMap[frequency] || 'monthly',
        state: state,
        filingStatus: watch('filingStatus') || 'single',
        dependents: 0,
        otherIncome: 0,
        deductions: businessExpenses,
        extraWithholding: 0
      });
      
      setValue('taxSummary', updatedTaxSummary);
    }
  }, [
    grossPay, frequency, state, setValue, watch,
    watch('homeOfficeSqft'),
    watch('totalHomeSqft'),
    watch('monthlyHomeExpenses'),
    watch('equipmentExpenses'),
    watch('professionalServices'),
    watch('travelExpenses'),
    watch('marketingExpenses'),
    watch('healthInsurance'),
    watch('otherExpenses'),
    watch('retirementContribution'),
    watch('hsaContribution'),
    watch('filingStatus'),
    watch('startDate'),
    watch('endDate'),
    watch('isActive')
  ]);

  // Format tax values for display
  const getFormattedTaxValue = (field) => {
    if (!grossPay || !frequency || !state || !taxSummary) return '';
    
    // Get the appropriate value from tax summary based on field name
    switch (field) {
      case 'federalTax':
        return formatCurrency(taxSummary.federalTax);
      case 'stateTax':
        return formatCurrency(taxSummary.stateTax);
      case 'selfEmploymentTax':
        return formatCurrency(taxSummary.fica);
      case 'totalTaxBurden':
        return formatCurrency(taxSummary.totalTax);
      case 'quarterlyTaxPayment':
        return formatCurrency(taxSummary.totalTax / 4);
      case 'takeHomePay':
        return formatCurrency(taxSummary.netIncome);
      default:
        return '';
    }
  };

  // Calculate gross pay from start date to end of year
  const calculatePartialYearGross = () => {
    const startDate = watch('startDate');
    const isActive = watch('isActive');
    const endDate = watch('endDate');
    
    if (!grossPay || !frequency || !startDate) return 0;
    
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return 0;
    
    // First, convert to annual amount based on frequency
    let annualizedAmount;
    switch (frequency) {
      case 'weekly':
        annualizedAmount = numGrossPay * 52;
        break;
      case 'biweekly':
        annualizedAmount = numGrossPay * 26;
        break;
      case 'monthly':
        annualizedAmount = numGrossPay * 12;
        break;
      case 'quarterly':
        annualizedAmount = numGrossPay * 4;
        break;
      case 'yearly':
        annualizedAmount = numGrossPay;
        break;
      default:
        annualizedAmount = numGrossPay * 12; // Default to monthly
    }
    
    // Now calculate the partial year if needed
    const start = new Date(startDate);
    const end = endDate && !isActive ? new Date(endDate) : new Date(new Date().getFullYear(), 11, 31);
    
    // If we have a full year or more, just return the annualized amount
    const yearDiff = end.getFullYear() - start.getFullYear();
    if (yearDiff > 1) {
      return annualizedAmount;
    }
    
    // For partial years, calculate the portion of the annual amount
    if (yearDiff === 0) {
      // Same year - calculate months between dates
      const monthDiff = end.getMonth() - start.getMonth() + 
                     (end.getDate() >= start.getDate() ? 1 : 0);
      return (monthDiff / 12) * annualizedAmount;
    } else if (yearDiff === 1) {
      // Different years - calculate remaining months in first year + months in second year
      const monthsFirstYear = 12 - start.getMonth() - (start.getDate() > 1 ? 0 : 1);
      const monthsSecondYear = end.getMonth() + (end.getDate() >= 1 ? 1 : 0);
      return ((monthsFirstYear + monthsSecondYear) / 12) * annualizedAmount;
    }
    
    return annualizedAmount;
  };

  // Calculate annual business expenses based on contract period
  const calculateAnnualBusinessExpenses = () => {
    const startDate = watch('startDate');
    const isActive = watch('isActive');
    const endDate = watch('endDate');
    
    if (!startDate || !calculateMonthlyBusinessExpenses()) return 0;
    
    // Use either provided end date or end of current year
    const start = new Date(startDate);
    const end = endDate && !isActive ? new Date(endDate) : new Date(new Date().getFullYear(), 11, 31);
    
    // Calculate number of months in the period
    const monthsInPeriod = Math.max(1, (end.getMonth() - start.getMonth()) + 
                        (end.getFullYear() - start.getFullYear()) * 12 + 
                        (end.getDate() >= start.getDate() ? 1 : 0));
    
    // Calculate total business expenses for the period using monthly equivalent
    return calculateMonthlyBusinessExpenses() * monthsInPeriod;
  };
  
  // Calculate net income after taxes and expenses
  const calculateNetIncome = () => {
    if (!grossPay || !frequency || !watch('startDate') || !taxSummary.totalTax) return 0;
    
    const annualGross = calculatePartialYearGross();
    const proportionalTaxes = taxSummary.totalTax * annualGross / taxSummary.annualGross;
    const annualExpenses = calculateAnnualBusinessExpenses();
    
    return annualGross - proportionalTaxes - annualExpenses;
  };

  // Calculate monthly net income directly instead of relying on annual calculation
  const calculateMonthlyNetIncome = () => {
    if (!grossPay || !frequency || !watch('startDate') || !taxSummary.totalTax) return 0;
    
    // Monthly gross pay
    const monthlyGross = frequency === 'monthly' 
      ? parseFloat(grossPay) 
      : calculatePartialYearGross() / 12;
    
    // Monthly business expenses (already frequency-aware)
    const monthlyExpenses = calculateMonthlyBusinessExpenses();
    
    // Monthly taxes
    const monthlyTaxes = taxSummary.totalTax * calculatePartialYearGross() / taxSummary.annualGross / 12;
    
    // Net income = Gross - Expenses - Taxes (ensure it's not negative)
    return Math.max(0, monthlyGross - monthlyExpenses - monthlyTaxes);
  };

  return (
    <>
      <FormSection title="Business Expenses">
        <div className="mb-4 bg-blue-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Understanding Business Expenses</h4>
          <p className="text-sm text-blue-700 mb-2">
            Tracking your business expenses can help reduce your taxable income. Common deductible expenses include:
          </p>
          <ul className="text-sm text-blue-700 space-y-1 pl-4 list-disc">
            <li>Home office expenses (if you have a dedicated workspace)</li>
            <li>Business equipment and supplies</li>
            <li>Professional services (legal, accounting, etc.)</li>
            <li>Business travel and vehicle expenses</li>
            <li>Health insurance premiums (if self-employed)</li>
            <li>Marketing and advertising costs</li>
          </ul>
          <p className="text-xs text-blue-600 mt-2 italic">
            Note: This information is for general guidance only. Consult a tax professional for specific advice.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Deductions</h5>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="Home Office Square Footage"
                name="homeOfficeSqft"
                type="number"
                placeholder="0"
                register={register}
                error={errors.homeOfficeSqft}
                tooltip="Enter the square footage of your dedicated home office space"
              />
              
              <FormField
                label="Total Home Square Footage"
                name="totalHomeSqft"
                type="number"
                placeholder="0"
                register={register}
                error={errors.totalHomeSqft}
                tooltip="Enter the total square footage of your home"
              />

              <FormField
                label="Monthly Home Expenses"
                name="monthlyHomeExpenses"
                type="text"
                prefix="$"
                placeholder="0.00"
                register={register}
                error={errors.monthlyHomeExpenses}
                tooltip="Total monthly home expenses (mortgage/rent, utilities, etc.) - always entered as monthly amount"
                onChange={handleBusinessExpensesChange}
                onBlur={handleBusinessExpensesBlur}
              />

              <FormField
                label="Home Office Deduction"
                name="homeOfficeDeduction"
                type="text"
                prefix="$"
                placeholder="0.00"
                value={calculateHomeOfficeDeduction().toFixed(2)}
                register={register}
                readOnly
                tooltip="Calculated home office deduction based on square footage"
              />
              
              <FormField
                label="SEP/Retirement Contribution"
                name="retirementContribution"
                type="text"
                prefix="$"
                placeholder="0.00"
                register={register}
                error={errors.retirementContribution}
                tooltip={`SEP IRA or retirement plan contributions (${getFrequencyLabel()})`}
                onChange={handleBusinessExpensesChange}
                onBlur={handleBusinessExpensesBlur}
              />
              
              <FormField
                label="Health Savings Account (HSA)"
                name="hsaContribution"
                type="text"
                prefix="$"
                placeholder="0.00"
                register={register}
                error={errors.hsaContribution}
                tooltip={`HSA contributions (${getFrequencyLabel()})`}
                onChange={handleBusinessExpensesChange}
                onBlur={handleBusinessExpensesBlur}
              />
            </div>
          </div>

          <FormField
            label="Business Equipment & Supplies"
            name="equipmentExpenses"
            type="text"
            prefix="$"
            placeholder="0.00"
            register={register}
            error={errors.equipmentExpenses}
            tooltip={`Cost of business equipment and supplies (${getFrequencyLabel()})`}
            onChange={handleBusinessExpensesChange}
            onBlur={handleBusinessExpensesBlur}
          />

          <FormField
            label="Professional Services"
            name="professionalServices"
            type="text"
            prefix="$"
            placeholder="0.00"
            register={register}
            error={errors.professionalServices}
            tooltip={`Cost of professional services (legal, accounting, etc.) (${getFrequencyLabel()})`}
            onChange={handleBusinessExpensesChange}
            onBlur={handleBusinessExpensesBlur}
          />

          <FormField
            label="Business Travel"
            name="travelExpenses"
            type="text"
            prefix="$"
            placeholder="0.00"
            register={register}
            error={errors.travelExpenses}
            tooltip={`Business travel expenses (${getFrequencyLabel()})`}
            onChange={handleBusinessExpensesChange}
            onBlur={handleBusinessExpensesBlur}
          />

          <FormField
            label="Marketing & Advertising"
            name="marketingExpenses"
            type="text"
            prefix="$"
            placeholder="0.00"
            register={register}
            error={errors.marketingExpenses}
            tooltip={`Marketing and advertising costs (${getFrequencyLabel()})`}
            onChange={handleBusinessExpensesChange}
            onBlur={handleBusinessExpensesBlur}
          />

          <FormField
            label="Health Insurance"
            name="healthInsurance"
            type="text"
            prefix="$"
            placeholder="0.00"
            register={register}
            error={errors.healthInsurance}
            tooltip={`Health insurance premiums (if self-employed) (${getFrequencyLabel()})`}
            onChange={handleBusinessExpensesChange}
            onBlur={handleBusinessExpensesBlur}
          />

          <FormField
            label="Other Business Expenses"
            name="otherExpenses"
            type="text"
            prefix="$"
            placeholder="0.00"
            register={register}
            error={errors.otherExpenses}
            tooltip={`Any other business expenses not listed above (${getFrequencyLabel()})`}
            onChange={handleBusinessExpensesChange}
            onBlur={handleBusinessExpensesBlur}
          />

          <div className="sm:col-span-2">
            <FormField
              label={`Total ${getFrequencyLabel().charAt(0).toUpperCase() + getFrequencyLabel().slice(1)} Business Expenses`}
              name="totalBusinessExpenses"
              type="text"
              prefix="$"
              placeholder="0.00"
              value={calculateTotalBusinessExpenses().toFixed(2)}
              register={register}
              readOnly
              tooltip={`Sum of all ${getFrequencyLabel()} business expenses including home office deduction`}
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Tax Planning">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label="Estimated Federal Tax"
            name="federalTax"
            type="text"
            prefix="$"
            placeholder="Select pay frequency"
            value={getFormattedTaxValue('federalTax')}
            register={register}
            readOnly
            tooltip="Estimated federal income tax based on adjusted gross income"
          />
          
          <FormField
            label="Self-Employment Tax"
            name="selfEmploymentTax"
            type="text"
            prefix="$"
            placeholder="Select pay frequency"
            value={getFormattedTaxValue('selfEmploymentTax')}
            register={register}
            readOnly
            tooltip="Social Security and Medicare taxes for self-employed individuals"
          />

          <FormField
            label="State Tax Estimate"
            name="stateTax"
            type="text"
            prefix="$"
            placeholder="Select state"
            value={getFormattedTaxValue('stateTax')}
            register={register}
            readOnly
            tooltip={`Estimated state income tax based on ${state || 'selected'} state rates`}
          />
          
          <FormField
            label="Total Tax Burden"
            name="totalTaxBurden"
            type="text"
            prefix="$"
            placeholder="Select pay frequency"
            value={getFormattedTaxValue('totalTaxBurden')}
            register={register}
            readOnly
            tooltip="Total estimated tax burden including federal, self-employment, and state taxes"
          />
          
          <FormField
            label="Estimated Quarterly Tax Payment"
            name="quarterlyTaxPayment"
            type="text"
            prefix="$"
            placeholder="Select pay frequency"
            value={getFormattedTaxValue('quarterlyTaxPayment')}
            register={register}
            readOnly
            tooltip="Estimated quarterly tax payment based on your total tax burden"
          />
          
          <FormField
            label="Estimate Net Pay"
            name="takeHomePay"
            type="text"
            prefix="$"
            placeholder="Select pay frequency"
            value={getFormattedTaxValue('takeHomePay')}
            register={register}
            readOnly
            tooltip="Estimated take-home pay after all taxes"
          />
        </div>
      </FormSection>

      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900">Income Comparison</h3>
        <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Income Details
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monthly Amount
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Annual Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Gross Pay
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                  {grossPay && frequency 
                    ? formatCurrency(
                        frequency === 'monthly' 
                          ? parseFloat(grossPay) 
                          : calculatePartialYearGross() / 12
                      )
                    : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                  {grossPay && frequency 
                    ? formatCurrency(calculatePartialYearGross())
                    : '-'}
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Business Expenses
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                  {calculateMonthlyBusinessExpenses() > 0 
                    ? formatCurrency(calculateMonthlyBusinessExpenses())
                    : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                  {calculateMonthlyBusinessExpenses() > 0 && watch('startDate')
                    ? formatCurrency(calculateAnnualBusinessExpenses())
                    : '-'}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Estimated Taxes
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                  {taxSummary.totalTax && grossPay && frequency
                    ? formatCurrency(taxSummary.totalTax * calculatePartialYearGross() / taxSummary.annualGross / 12)
                    : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                  {taxSummary.totalTax && grossPay && frequency
                    ? formatCurrency(taxSummary.totalTax * calculatePartialYearGross() / taxSummary.annualGross)
                    : '-'}
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  Net Income (After Taxes & Expenses)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-blue-600">
                  {grossPay && frequency && taxSummary.netIncome && watch('startDate')
                    ? formatCurrency(calculateMonthlyNetIncome())
                    : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-blue-600">
                  {grossPay && frequency && taxSummary.netIncome && watch('startDate')
                    ? formatCurrency(calculateNetIncome())
                    : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default TaxPlanningSection; 